import { ArrowUpRight, ArrowRight, MapPinned, RefreshCw } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { presetRequest } from "./QuoteSection";

const CYCLE = ["Forecast", "Source", "Compare", "Approve", "Deliver", "Repeat"];

export default function ManagedSupply() {
  return (
    <section id="managed-supply" data-testid="managed-supply-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_30%,rgba(217,164,65,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionTag number="06" label="Managed Supply" />
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white max-w-2xl">
              Stop repeating the same <span className="text-gold">procurement work.</span>
            </h2>
            <p className="text-white/55 max-w-md text-base leading-relaxed">
              Businesses with recurring energy requirements shouldn&rsquo;t have to search for
              suppliers, request quotations and coordinate supply from scratch every week or
              month.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="glass-panel rounded-2xl p-8 md:p-10 h-full" data-testid="managed-procurement-card">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </span>
                <h3 className="font-outfit text-2xl font-light text-white">Managed Energy Procurement</h3>
              </div>
              <div className="flex flex-wrap items-center gap-y-4" data-testid="managed-cycle">
                {CYCLE.map((step, i) => (
                  <span key={step} className="flex items-center">
                    <span className="font-outfit text-sm tracking-wide text-white/75 border border-white/12 rounded-full px-4 py-2 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                      {step}
                    </span>
                    {i < CYCLE.length - 1 && <ArrowRight className="w-4 h-4 text-gold/60 mx-2" />}
                    {i === CYCLE.length - 1 && <RefreshCw className="w-3.5 h-3.5 text-gold/60 mx-2" />}
                  </span>
                ))}
              </div>
              <p className="text-white/55 text-sm md:text-base leading-relaxed mt-8">
                Ajemi acts as your procurement partner for recurring requirements — planning
                supply cycles, sourcing through the network, coordinating each transaction and
                reviewing performance with you over time.
              </p>
              <p className="text-white/35 text-xs leading-relaxed mt-4">
                Pricing, availability and credit terms are agreed per commercial arrangement.
                Ajemi does not promise fixed pricing or guaranteed availability unless
                specifically agreed.
              </p>
              <button
                data-testid="managed-discuss-btn"
                onClick={() => presetRequest({ type: "recurring" })}
                className="group mt-8 inline-flex items-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full pl-7 pr-2.5 py-2.5 hover:bg-goldbright hover:shadow-[0_0_40px_rgba(217,164,65,0.45)] transition-all duration-300"
              >
                Set Up Managed Supply
                <span className="bg-ink/10 rounded-full p-2 group-hover:rotate-45 transition-transform duration-300">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="glass-panel rounded-2xl p-8 md:p-10 h-full" data-testid="multi-location-card">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <MapPinned className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </span>
                <h3 className="font-outfit text-2xl font-light text-white">
                  One Procurement Partner. <span className="text-gold">Multiple Locations.</span>
                </h3>
              </div>
              <p className="text-white/55 text-sm md:text-base leading-relaxed">
                Businesses operating across several sites can work through one procurement
                relationship — instead of managing multiple suppliers, quotations and delivery
                arrangements in every location.
              </p>
              <div className="mt-8 space-y-4" data-testid="multi-location-points">
                {[
                  "One request covering multiple delivery locations",
                  "Coordinated volumes, schedules and documentation",
                  "A single point of contact for your supply requirements",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold mt-2 shrink-0" />
                    <span className="text-sm text-white/60">{t}</span>
                  </div>
                ))}
              </div>
              <button
                data-testid="multi-location-manage-btn"
                onClick={() => presetRequest({ type: "recurring", note: "Multi-location supply requirement" })}
                className="group mt-8 inline-flex items-center gap-3 border border-gold/50 text-gold font-outfit rounded-full pl-7 pr-2.5 py-2.5 hover:bg-gold hover:text-ink transition-all duration-300"
              >
                Manage My Supply
                <span className="bg-gold/15 rounded-full p-2 group-hover:bg-ink/10 group-hover:rotate-45 transition-all duration-300">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
